/**
 * Export Service for PDF and Image exports with progress tracking
 * Now uses queue-based system for images (JPG, PNG, TIF) for better scalability
 */

import imageExportService from './imageExportService';

class ExportService {
  constructor() {
    this.baseUrl = (process.env.REACT_APP_API_BASE || 'https://template.rajoffset.com') + '/api/v1';
    this.activeJobs = new Map(); // jobId -> job data
  }

  /**
   * Start export job with progress tracking
   * @param {Object} template_content - The template content to render
   * @param {string} format - Export format (pdf, png, jpg, tif)
   * @param {number} currentPageIndex - Current page index (optional)
   * @param {string} colorMode - Color mode (RGB/CMYK) (optional)
   * @param {string} userId - User ID (optional)
   * @param {string} templateId - Template ID (optional)
   * @returns {Promise<{success: boolean, jobId: string}>}
   */
  async startExport(template_content, format, currentPageIndex = null, colorMode = null, userId = 'default-user', templateId = null, customerId = null, employeeId = null) {
    try {
      // Use queue system for image and PDF formats
      if (['jpg', 'jpeg', 'png', 'tif', 'tiff', 'pdf'].includes(format.toLowerCase())) {
        console.log(`🔄 Using queue system for ${format.toUpperCase()} export`);
        
        const result = await imageExportService.startImageExport(
          template_content,
          userId,
          format,
          templateId,
          'production', // sample_type
          currentPageIndex,
          colorMode,
          customerId,
          employeeId
        );

        if (result.success) {
          // Store job info for tracking
          const jobData = {
            id: result.queueId,
            status: 'queued',
            progress: 0,
            format: format.toUpperCase(),
            currentPageIndex,
            colorMode,
            templateId,
            customerId,
            employeeId,
            startTime: Date.now(),
            created_at: new Date().toISOString(),
            isQueueJob: true
          };

          this.activeJobs.set(result.queueId, jobData);

          // Start polling for status updates
          this.pollQueueJobStatus(result.queueId);

          return {
            success: true,
            jobId: result.queueId,
            message: `Started ${format.toUpperCase()} export via queue`,
            isQueueJob: true
          };
        } else {
          throw new Error(result.error || 'Failed to start queue job');
        }
      }

      // All exports must go through the workflow queue. Any other format is unsupported
      // (the previous direct/synchronous export path has been retired so that every
      // render is dispatched to the distributed video_worker fleet via the workflow queue).
      throw new Error(`Unsupported export format "${format}". Only jpg, png, pdf and tif are supported (all via the workflow queue).`);
    } catch (error) {
      console.error('Error starting export:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Load a user's recent export jobs from the server and seed the local tracking +
   * bottom-right panel. Resumes polling for any job that is still in flight so the UI
   * keeps updating after a page reload. Emits `export:progress` for each job so the
   * existing ExportProgress/FooterProgressIndicator components render them.
   * @param {string} userId
   * @param {number} limit
   * @returns {Promise<Array>} the mapped jobs
   */
  async loadUserJobs(userId, limit = 25) {
    if (!userId) return [];
    try {
      const data = await imageExportService.getUserQueueJobs(userId, limit);
      const jobs = Array.isArray(data?.jobs) ? data.jobs : [];
      const mapped = [];

      for (const job of jobs) {
        const completedUrl = job.primary_final_url || job.final_url || job.image_url || null;
        const mappedJob = {
          id: job.id,
          status: job.status,
          progress: job.progress || 0,
          progressText: job.progress_text,
          errorMessage: job.error_message,
          jobType: job.job_type,
          format: (job.format || '').toUpperCase(),
          currentPageIndex: job.current_page_index,
          colorMode: job.color_mode,
          templateId: job.template_id,
          customerId: job.customer_id,
          employeeId: job.employee_id,
          created_at: job.created_at,
          completedAt: job.completed_at,
          isQueueJob: true,
          downloadUrl: completedUrl,
          image_url: job.image_url || completedUrl,
          final_url: completedUrl,
          thumbnail_url: job.primary_thumbnail_url || job.thumbnail_url || null,
        };
        if (completedUrl) {
          mappedJob.filename = this.generateFilename(mappedJob.format, mappedJob.currentPageIndex, mappedJob.colorMode);
        }
        // Task 4: jobs created over an hour ago and still not terminal are seeded as
        // "suspended" so they don't auto-poll after a reload (user can Sync manually).
        const createdMs = job.created_at ? Date.parse(job.created_at) : NaN;
        const isTerminal = ['completed', 'failed', 'cancelled'].includes(String(job.status).toLowerCase());
        const isStale = Number.isFinite(createdMs) && (Date.now() - createdMs) > (60 * 60 * 1000);
        if (!isTerminal && isStale) {
          mappedJob.status = 'suspended';
          mappedJob.progressText = 'Suspended: pending over 1 hour. Use Sync to check again.';
        }

        this.activeJobs.set(job.id, mappedJob);
        mapped.push(mappedJob);

        // Notify the panel.
        window.dispatchEvent(new CustomEvent('export:progress', {
          detail: { jobId: job.id, job: mappedJob }
        }));

        // Resume polling only for non-terminal, non-suspended jobs.
        const inFlight = !isTerminal && !(mappedJob.status === 'suspended');
        if (inFlight) {
          this.pollQueueJobStatus(job.id);
        }
      }
      return mapped;
    } catch (error) {
      console.warn('Failed to load user export jobs:', error);
      return [];
    }
  }

  /**
   * Poll queue job status for image exports
   * @param {number} queueId - Queue job ID
   */
  async pollQueueJobStatus(queueId) {
    const pollInterval = 2000; // Poll every 2 seconds
    const maxPollTime = 900000; // Max 15 minutes
    const suspendAfterMs = 60 * 60 * 1000; // Suspend pending jobs older than 1 hour
    const startTime = Date.now();

    // A job's true age is based on its server created_at when available (survives reloads),
    // else the local start time. Used for the 1-hour suspend rule.
    const jobCreatedMs = (() => {
      const j = this.activeJobs.get(queueId);
      const ts = j?.created_at ? Date.parse(j.created_at) : (j?.startTime || startTime);
      return Number.isFinite(ts) ? ts : startTime;
    })();

    const poll = async () => {
      try {
        // Stop polling if the job was cancelled/suspended or removed locally.
        const existing = this.activeJobs.get(queueId);
        if (!existing || ['cancelled', 'suspended'].includes(existing.status)) {
          return;
        }

        // Task 4: suspend jobs that have been pending for over an hour (stop polling).
        const stillPending = !['completed', 'failed', 'cancelled'].includes(String(existing.status).toLowerCase());
        if (stillPending && (Date.now() - jobCreatedMs) > suspendAfterMs) {
          this.updateJobStatus(queueId, 'suspended', existing.progress || 0,
            'Suspended: pending over 1 hour. Use Sync to check again.', { nextPollAt: null });
          return; // Stop polling
        }

        const result = await imageExportService.getJobStatus(queueId);
        
        if (result.success) {
          const job = result.job;
          const localJob = this.activeJobs.get(queueId);
          
          if (localJob && localJob.status !== 'cancelled') {
            // Update local job data
            localJob.status = job.status;
            localJob.progress = job.progress || 0;
            localJob.progressText = job.progress_text;
            localJob.errorMessage = job.error_message;
            localJob.completedAt = job.completed_at;

            // Check if job is completed
            const completedUrl = job.final_url || job.image_url;
            if (job.status === 'completed' && completedUrl) {
              localJob.downloadUrl = completedUrl;
              localJob.image_url = job.image_url || completedUrl; // Store for preview
              localJob.final_url = completedUrl; // Store for preview
              localJob.filename = this.generateFilename(localJob.format, localJob.currentPageIndex, localJob.colorMode);
              this.updateJobStatus(queueId, 'completed', 100, 'Export completed!', {
                downloadUrl: completedUrl,
                image_url: job.image_url || completedUrl,
                final_url: completedUrl,
                filename: localJob.filename
              });
              return; // Stop polling
            } else if (job.status === 'failed') {
              this.updateJobStatus(queueId, 'failed', 0, `Export failed: ${job.error_message || 'Unknown error'}`);
              return; // Stop polling
            } else {
              // Update progress and continue polling. Record when the next poll will fire
              // so the UI can show a countdown timer on pending items (Task 3).
              this.updateJobStatus(queueId, job.status, job.progress || 0, job.progress_text, {
                nextPollAt: Date.now() + pollInterval
              });
            }
          }
        }

        // Continue polling if not completed and within time limit
        if (Date.now() - startTime < maxPollTime) {
          setTimeout(poll, pollInterval);
        } else {
          this.updateJobStatus(queueId, 'failed', 0, 'Export timeout after 15 minutes - please try again');
        }
      } catch (error) {
        console.error('Error polling queue job status:', error);
        this.updateJobStatus(queueId, 'failed', 0, `Polling error: ${error.message}`);
      }
    };

    // Start polling
    poll();
  }

  /**
   * Task 1: Manually sync a single job's status from the workflow queue (one-shot).
   * If the job is still in flight after syncing, (re)start its polling loop.
   * @param {number} queueId
   * @returns {Promise<Object|null>} the updated job
   */
  async syncJob(queueId) {
    try {
      const result = await imageExportService.getJobStatus(queueId);
      if (!result.success) return null;
      const job = result.job;
      const completedUrl = job.final_url || job.image_url;
      const status = String(job.status || '').toLowerCase();

      if (status === 'completed' && completedUrl) {
        const localJob = this.activeJobs.get(queueId) || {};
        const filename = this.generateFilename(localJob.format || (job.format || '').toUpperCase(), localJob.currentPageIndex, localJob.colorMode);
        this.updateJobStatus(queueId, 'completed', 100, 'Export completed!', {
          downloadUrl: completedUrl,
          image_url: job.image_url || completedUrl,
          final_url: completedUrl,
          filename,
          nextPollAt: null,
        });
      } else if (status === 'failed') {
        this.updateJobStatus(queueId, 'failed', 0, `Export failed: ${job.error_message || 'Unknown error'}`, { nextPollAt: null });
      } else {
        // Still pending/processing → reflect latest and resume polling.
        this.updateJobStatus(queueId, job.status, job.progress || 0, job.progress_text, {
          nextPollAt: Date.now() + 2000,
        });
        this.pollQueueJobStatus(queueId);
      }
      return this.activeJobs.get(queueId) || null;
    } catch (error) {
      console.warn('syncJob failed:', error);
      return null;
    }
  }

  /**
   * Task 1: Manually sync ALL jobs that aren't in a terminal state (pending/processing/
   * suspended). Suspended jobs are revived by syncing.
   * @returns {Promise<number>} count of jobs synced
   */
  async syncAllActive() {
    const targets = Array.from(this.activeJobs.values()).filter(
      (j) => !['completed', 'failed', 'cancelled'].includes(String(j.status).toLowerCase())
    );
    await Promise.all(targets.map((j) => this.syncJob(j.id)));
    return targets.length;
  }

  /**
   * Task 2: Clear all queue records for a user (server + local). Optionally scope to a template.
   * @param {string} userId
   * @param {string|null} templateId
   * @returns {Promise<{success:boolean, deleted?:number, error?:string}>}
   */
  async clearUserJobs(userId, templateId = null) {
    if (!userId) return { success: false, error: 'Missing user id' };
    // Delegate to imageExportService so we reuse its (correct, same-origin) base URL.
    const data = await imageExportService.clearUserJobs(userId, templateId);
    const ok = Boolean(data?.success || (typeof data?.status === 'string' && data.status.toLowerCase() === 'success'));
    if (ok) {
      // Clear local tracking regardless (the panel is client-driven).
      this.activeJobs.clear();
      imageExportService.getActiveJobs().clear();
      return { success: true, deleted: data.deleted };
    }
    return { success: false, error: data?.error || 'Failed to clear queue' };
  }

  /**
   * Update job status and notify listeners
   * @param {string} jobId - Job ID
   * @param {string} status - New status
   * @param {number} progress - Progress percentage
   * @param {string} message - Status message
   * @param {Object} additionalData - Additional job data
   */
  updateJobStatus(jobId, status, progress, message, additionalData = {}) {
    const job = this.activeJobs.get(jobId);
    if (!job) return;

    const updatedJob = {
      ...job,
      status,
      progress,
      message,
      ...additionalData
    };

    this.activeJobs.set(jobId, updatedJob);

    // Emit update event
    window.dispatchEvent(new CustomEvent('export:progress', {
      detail: { jobId, job: updatedJob }
    }));
  }

  /**
   * Get job by ID
   * @param {string} jobId - Job ID
   * @returns {Object|null} Job data
   */
  getJob(jobId) {
    return this.activeJobs.get(jobId) || null;
  }

  /**
   * Get all active jobs
   * @returns {Array} Active jobs
   */
  getActiveJobs() {
    return Array.from(this.activeJobs.values());
  }

  /**
   * Cancel a job. Marks it cancelled locally (which stops the UI treating it as active)
   * and asks the backend/workflow queue to cancel the underlying job.
   * @param {string} jobId - Job ID (queue id)
   * @returns {boolean} Success status
   */
  cancelJob(jobId) {
    const job = this.activeJobs.get(jobId);
    if (job && !['completed', 'failed', 'cancelled'].includes(job.status)) {
      // Optimistically mark cancelled so polling callbacks stop updating the UI.
      this.updateJobStatus(jobId, 'cancelled', job.progress || 0, 'Export cancelled');
      // Fire-and-forget backend cancellation of the queued/processing job.
      imageExportService.cancelJob(jobId).catch((err) => {
        console.warn('Failed to cancel queue job on server:', err);
      });
      return true;
    }
    return false;
  }

  /**
   * Remove a job from tracking
   * @param {string} jobId - Job ID
   */
  removeJob(jobId) {
    this.activeJobs.delete(jobId);
  }

  /**
   * Clear completed jobs from local cache
   */
  clearCompletedJobs() {
    for (const [jobId, job] of this.activeJobs.entries()) {
      if (['completed', 'failed', 'cancelled'].includes(job.status)) {
        this.activeJobs.delete(jobId);
      }
    }
  }

  /**
   * Generate filename for export
   * @param {string} format - Export format
   * @param {number} currentPageIndex - Current page index
   * @param {string} colorMode - Color mode (for TIF)
   * @returns {string} Generated filename
   */
  generateFilename(format, currentPageIndex, colorMode = null) {
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const formatLower = format.toLowerCase();
    
    if (['tif', 'png', 'jpg', 'jpeg'].includes(formatLower) && colorMode) {
      // Image formats with color mode
      if (currentPageIndex !== null && currentPageIndex !== undefined) {
        return `design-page-${currentPageIndex + 1}-${colorMode.toLowerCase()}-${timestamp}.${formatLower}`;
      }
      return `design-all-${colorMode.toLowerCase()}-${timestamp}.${formatLower}`;
    } else {
      // Standard formats without color mode
      if (currentPageIndex !== null && currentPageIndex !== undefined) {
        return `design-page-${currentPageIndex + 1}-${timestamp}.${formatLower}`;
      }
      return `design-all-${timestamp}.${formatLower}`;
    }
  }

  /**
   * Utility function to add delay
   * @param {number} ms - Milliseconds to delay
   * @returns {Promise} Promise that resolves after delay
   */
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

// Create singleton instance
const exportService = new ExportService();

export default exportService;
